import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import {
  changeColorClass,
  formatDate,
  formatDateTime,
  formatKrw,
  formatPercent,
  formatSince,
  formatUsd,
} from '@/lib/format';
import { AccountNav } from './AccountNav';
import {
  conditionLegLabel,
  conditionalStatusLabel,
  conditionalTypeLabel,
  costGap,
  feePercent,
  holdingGuideLine,
  isSnapshotStale,
  isSyncFailureCurrent,
  latestSyncedAt,
  sideLabel,
  sideTone,
  snapshotAge,
  statusLabel,
  statusTone,
} from './accountDisplay';
import type { Holding, HoldingTotal, TradeOrder, TradingAccount } from './accountTypes';
import {
  useAccountFunds,
  useAccountSummary,
  useConditionalOrders,
  useHoldingGuides,
  useOrders,
  useRefreshAccount,
} from './useAccount';
import { StockLink } from './StockLink';
import { PnlCurveCard } from './PnlCurveCard';

/**
 * 계좌·보유·체결 화면. (12단계)
 *
 * 🔴 이 화면에는 실제 자산이 보인다
 *   TRADING_ACCOUNT 메뉴 권한이 있어야 열린다. 추천 화면(TRADING) 권한과 별개다.
 *   토스 API 키가 하나뿐이라 여기 보이는 것은 전부 사용자 본인 계좌다.
 *   (00-architecture.md 1절)
 *
 * 🔴 숫자를 만들지 않는다
 *   평가금액·손익·손익률은 전부 토스가 준 값이거나 백엔드가 합산한 값이다.
 *   화면에서 다시 계산하지 않는다. 두 곳에서 계산하면 반드시 갈라지고,
 *   그때 어느 쪽을 믿어야 할지 알 수 없어진다.
 *
 * 화면이 하는 일은 세 가지다.
 *   언제 기준의 값인지 밝히기
 *   통화를 섞지 않기
 *   체결된 것과 안 된 것을 구분하기
 */
export function AccountPage() {
  const { data, isLoading, error, refetch } = useAccountSummary();

  /*
    🔴 로딩·오류 화면에도 탭을 남긴다
      탭을 본문 안에만 두면, 계좌 조회가 느리거나 실패하는 동안 매매일지로
      건너갈 길이 사라진다. 두 화면은 같은 권한 아래 나란히 있는 것이지
      계좌가 성공해야 열리는 관계가 아니다.
  */
  if (isLoading || error || !data) {
    return (
      <div className="space-y-4">
        <AccountNav />
        {isLoading && <Spinner label="계좌를 불러오는 중" />}
        {error && <ErrorState message={error.message} onRetry={() => void refetch()} />}
      </div>
    );
  }

  const 동기화전 = data.accounts.length === 0 && data.snapshotDate === null;

  return (
    <div className="space-y-4">
      <AccountNav />
      <SyncBar accounts={data.accounts} syncFailedAt={data.syncFailedAt} />
      <FundsCard />

      {동기화전 ? (
        <EmptyState
          title="아직 동기화되지 않았습니다."
          description="첫 동기화 전에는 비어 있는 것이 정상입니다. 위 [지금 동기화]를 누르면 바로 받고, 그 뒤로는 이 화면을 열어 두면 1분마다, 배치가 평일 16:00·20:10과 매일 06:10(미국장 뒤)에 받습니다."
        />
      ) : (
        <>
          <AccountList accounts={data.accounts} />
          <SnapshotNotice snapshotDate={data.snapshotDate} />
          <TotalCards totals={data.totals} />
          <HoldingTable holdings={data.holdings} snapshotDate={data.snapshotDate} />
        </>
      )}

      <PnlCurveCard />
      <ConditionalOrderList />
      <OrderHistory />
    </div>
  );
}

/**
 * 걸어 둔 조건주문 — 조회만. (2026-10-16, docs/impl/150 4번 · autotrade 규칙 ⓐ)
 *
 * 🔴 조건주문은 앱이 꺼져 있어도 체결된다 — 토스 앱에서 건 손절 · 익절도 여기 다 보여야 "무엇이 걸려 있나" 를 안다.
 * 🔴 이 화면에서 걸거나 취소하지 않는다. 취소는 토스 앱에서 한다(주문 모듈은 A4 · autotrade 한 곳).
 * 빈 목록과 "못 받음" 을 가른다 — 못 받았는데 "없습니다" 라고 하면 걸린 손절을 잊는다.
 */
function ConditionalOrderList() {
  const { data, error } = useConditionalOrders();

  return (
    <Card title={`걸어 둔 조건주문${data && data.items.length > 0 ? ` ${data.items.length}` : ''}`}>
      {error && <p className="text-sm text-warn">불러오지 못했습니다. {error.message}</p>}
      {data?.failed && (
        <p className="mb-2 text-xs text-warn">
          토스에서 다시 받지 못했습니다{data.fetchedAt ? ` — 아래는 ${formatDateTime(data.fetchedAt)} 에 받은 목록입니다` : ''}.
        </p>
      )}
      {data && data.items.length === 0 && !data.failed && (
        <p className="text-sm text-fg-muted">걸어 둔 조건주문이 없습니다. 토스 앱에서 건 것도 여기 함께 보입니다.</p>
      )}
      {data && data.items.length > 0 && (
        <ul className="divide-y divide-line">
          {data.items.map((order) => {
            const price = (value: number) => money(value, order.market === 'US' ? 'USD' : 'KRW');
            return (
              <li key={order.conditionalOrderId} className="space-y-1 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-fg">{order.name}</span>
                  <Badge tone="info">{conditionalTypeLabel(order.type)}</Badge>
                  <Badge tone={order.status === 'PAUSED' ? 'warning' : 'neutral'}>{conditionalStatusLabel(order.status)}</Badge>
                  <span className="tabular text-xs text-fg-muted">{order.quantity}주 · {formatDate(order.expireDate)}까지</span>
                </div>
                <p className="text-xs text-fg-muted">{conditionLegLabel(order.first, price)}</p>
                {order.second && <p className="text-xs text-fg-muted">{conditionLegLabel(order.second, price)}</p>}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 text-xs text-fg-subtle">조회만 합니다. 걸기 · 고치기 · 취소는 토스 앱에서 하세요.</p>
    </Card>
  );
}

/**
 * 동기화 줄 — 마지막 동기화 · [지금 동기화] · 실패 안내. (2026-10-16, docs/impl/150 1번)
 *
 * 🔴 마지막 동기화 = 계좌들의 syncedAt 중 가장 늦은 것
 *   배치(평일 16:00 · 20:10 · 매일 06:10)와 화면 요청(열려 있는 동안 1분마다) 둘 다 이 값을 고친다.
 *   그래서 "이 화면의 숫자가 몇 분 전 토스 값인가" 에 바로 답한다. 스냅샷 기준일(SnapshotNotice)은 날짜만 말한다.
 *
 * 🔴 실패해도 화면은 저장된 값을 보여 준다(서버가 실패를 삼킨다) — 그래서 여기서 밝힌다.
 *   실패 시각이 마지막 동기화보다 늦을 때만 띄운다. 그 뒤에 배치가 성공했으면 값은 다시 최신이다.
 */
function SyncBar({
  accounts,
  syncFailedAt,
}: {
  accounts: TradingAccount[];
  syncFailedAt: string | null;
}) {
  const refresh = useRefreshAccount();
  const lastSynced = latestSyncedAt(accounts);
  const failedAfterSync = isSyncFailureCurrent(lastSynced, syncFailedAt);

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>
          {lastSynced
            ? `마지막 동기화 ${formatSince(lastSynced)} (${formatDateTime(lastSynced)})`
            : '아직 토스에서 받은 적이 없습니다'}
        </span>
        <Button
          variant="secondary"
          className="px-2 py-1 text-xs"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          {refresh.isPending ? '동기화 중…' : '지금 동기화'}
        </Button>
      </div>
      <p className="text-xs text-fg-subtle">
        이 화면을 열어 두면 1분마다 저절로 다시 받습니다. 버튼은 토스 앱에서 방금 체결된 주문 · 보유를 바로 볼 때 누르세요(10초에 한 번까지 · 걸어 둔 조건주문은 1분 주기로 따로 받습니다).
      </p>
      {(failedAfterSync || refresh.error) && (
        <p className="text-xs text-warn">
          토스에서 다시 받지 못했습니다{syncFailedAt ? ` (${formatDateTime(syncFailedAt)})` : ''}. 아래 숫자는 마지막으로 받은 값입니다.
        </p>
      )}
    </div>
  );
}

/**
 * 돈 카드 — 주문 가능 현금 · 수수료. (2026-10-16, docs/impl/150 2 · 3번)
 *
 * 🔴 주문 가능 현금은 예수금과 다를 수 있다 — 토스 buying-power 의 "미수 없이 현금으로 살 수 있는 금액" 이다.
 * 🔴 수수료가 모의투자 계산 요율과 다르면 그 자리에서 밝힌다 — 미국 0.1% 같은 행사 요율이 끝나면
 *   모의 성적이 실제보다 좋아 보인다(서버 FeeWatchJob 이 매일 08:20 Slack 으로도 알린다).
 * 요약과 따로 부른다 — 다른 토스 호출 한도 묶음이라 따로 실패할 수 있고, 그래도 보유 표는 열려야 한다.
 */
function FundsCard() {
  const { data, error } = useAccountFunds();

  if (error) {
    return <p className="text-xs text-warn">주문 가능 현금 · 수수료를 불러오지 못했습니다. {error.message}</p>;
  }
  if (!data) {
    return null;
  }

  const today = new Date();
  return (
    <Card title="주문 가능 현금 · 수수료">
      {data.cash.length === 0 ? (
        <p className="text-sm text-fg-muted">아직 받은 값이 없습니다. 첫 동기화 때 채워집니다.</p>
      ) : (
        <ul className="space-y-1">
          {data.cash.map((line) => (
            <li key={line.currency} className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="text-fg-muted">{line.currency === 'USD' ? '달러' : '원화'}</span>
              <span className="tabular font-medium text-fg">{money(line.cashBuyingPower, line.currency)}</span>
              <span className="text-xs text-fg-subtle">
                {snapshotAge(line.snapshotDate, today) === '오늘'
                  ? formatSince(line.syncedAt)
                  : `${formatDate(line.snapshotDate)} 값`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-fg-subtle">
        미수 없이 현금만으로 살 수 있는 금액입니다(토스 기준). 결제 전 금액이 섞인 예수금과 다를 수 있습니다.
      </p>

      {data.fees.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-line pt-3">
          {data.fees.map((fee) => (
            <li key={fee.market} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="text-fg-muted">{fee.market} 수수료</span>
              <span className="tabular text-fg">{feePercent(fee.rate)}</span>
              {fee.endDate && <span className="text-xs text-fg-subtle">{formatDate(fee.endDate)}까지</span>}
              {fee.paperRate !== null && fee.paperRate !== fee.rate && (
                <Badge tone="warning">모의투자는 {feePercent(fee.paperRate)}로 계산 중</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.fees.length > 0 && (
        <p className="mt-2 text-xs text-fg-subtle">
          수수료는 토스에서 6시간마다 다시 받습니다. 매일 08:20 확인에서 행사 요율이 끝나 가거나 모의투자 요율과 다르면 관리자에게 알립니다.
        </p>
      )}
    </Card>
  );
}

/**
 * 계좌 목록.
 *
 * 계좌가 하나뿐이라 표가 아니라 한 줄로 늘어놓는다.
 * 해지된 계좌도 사라지지 않고 남는다 - 그 계좌로 했던 거래의 맥락이 필요하기 때문이다.
 */
function AccountList({ accounts }: { accounts: TradingAccount[] }) {
  if (accounts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {accounts.map((account) => (
        <div
          key={account.maskedAccountNo + account.accountType}
          className={cx(
            'flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2',
            account.stale && 'opacity-60',
          )}
        >
          <span className="tabular text-sm text-fg">{account.maskedAccountNo}</span>
          <Badge tone={account.brokerage ? 'info' : 'neutral'}>{account.accountType}</Badge>
          {account.stale && <Badge tone="warning">갱신 끊김</Badge>}
        </div>
      ))}
    </div>
  );
}

/**
 * 이 화면의 숫자가 언제 기준인지 밝힌다.
 *
 * 🔴 화면에서 가장 중요한 한 줄일 수 있다
 *   주말이나 연휴에 열면 금요일 값이 그대로 떠 있다.
 *   그것을 오늘 값으로 읽으면 판단이 며칠 밀린다.
 *
 * 🔴 2026-09-20 (I-76) — "평일 16:00 에만 돈다" 는 전제가 깨졌다
 *   지금은 둘이다: 화면이 열려 있는 동안 60초마다(refreshIfStale) · 확정 스냅샷 배치(평일 16:00 · 20:10, 매일 06:10).
 *   ✏️ 9/28 I-78 2차 — "주문 직후(refreshNow)" 는 호출처가 0 이라 뺐다(자동매매 주문 모듈 A4 전).
 *   다만 이 안내 자체는 여전히 필요하다 — 국내 장이 닫힌 주말에는 국내 값이 새로 생기지 않아
 *   금요일 스냅샷이 그대로 떠 있기 때문이다(토요일 06:10 은 미국 금요일 장만 들어온다).
 */
function SnapshotNotice({ snapshotDate }: { snapshotDate: string | null }) {
  if (!snapshotDate) {
    return null;
  }

  const today = new Date();
  const age = snapshotAge(snapshotDate, today);
  const stale = isSnapshotStale(snapshotDate, today);

  return (
    <p className={cx('text-xs', stale ? 'text-warn' : 'text-fg-muted')}>
      {formatDate(snapshotDate)} 기준 ({age})
      {/* isSnapshotStale 은 4일째부터(days > 3)다 — "나흘 넘게" 는 5일째로 읽혀 하루 어긋났다 (9/28) */}
      {stale && ' · 나흘째 갱신되지 않았습니다. 배치를 확인하세요'}
    </p>
  );
}

/**
 * 통화별 합계 카드.
 *
 * 🔴 통화를 섞어 더한 카드를 만들지 않는다
 *   백엔드가 통화별로 갈라 보내주고, 화면은 온 대로 하나씩 그린다.
 *   지금은 국내 종목뿐이라 KRW 카드 하나다.
 */
function TotalCards({ totals }: { totals: HoldingTotal[] }) {
  if (totals.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {totals.map((total) => (
        <Card key={total.currency}>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-fg-subtle">
              {total.currency} · {total.count}종목
            </span>
            <span className={cx('text-sm font-medium', changeColorClass(total.profitRate))}>
              {formatPercent(total.profitRate)}
            </span>
          </div>

          <p className="mt-1 text-xl font-semibold tabular text-fg">
            {money(total.marketValue, total.currency)}
          </p>

          <dl className="mt-2 space-y-1 text-xs">
            <Row label="매입" value={money(total.purchaseAmount, total.currency)} />
            <Row
              label="평가손익"
              value={money(total.profitLoss, total.currency)}
              className={changeColorClass(total.profitLoss)}
            />
            {/*
              순액은 세금·수수료를 뺀 값이다. 지금 팔면 실제로 손에 쥐는 금액이라
              단타에서는 이쪽이 진짜 숫자에 가깝다.
            */}
            <Row
              label="순손익 (세금·수수료 차감)"
              value={money(total.profitLossNet, total.currency)}
              className={changeColorClass(total.profitLossNet)}
            />
          </dl>
        </Card>
      ))}
    </div>
  );
}

function Row({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className={cx('tabular', className ?? 'text-fg')}>{value}</dd>
    </div>
  );
}

/** 보유 종목 표. */
function HoldingTable({
  holdings,
  snapshotDate,
}: {
  holdings: Holding[];
  snapshotDate: string | null;
}) {
  // 기준선(150번 5번)은 따로 부른다 — 실패해도 보유 표는 그대로 그린다(줄만 빠진다)
  const { data: guideList } = useHoldingGuides();
  const guides = new Map((guideList ?? []).map((guide) => [guide.symbol, guide]));

  if (holdings.length === 0) {
    return (
      <Card title="보유 종목">
        {/*
          보유 0건과 배치 실패는 다르다. 기준일이 있으면 전자다 -
          그날 전량 매도했다는 뜻이므로 오류로 보여주면 안 된다.
        */}
        <EmptyState
          title={snapshotDate ? '보유 중인 종목이 없습니다.' : '보유 스냅샷이 없습니다.'}
          description={
            snapshotDate
              ? '해당 기준일에 보유한 종목이 없습니다.'
              : '이 화면을 열어 두면 1분마다 갱신되고, 바로 받으려면 위 [지금 동기화]를 누르세요. 확정 스냅샷은 평일 16:00·20:10과 매일 06:10(미국장 뒤)입니다.'
          }
        />
      </Card>
    );
  }

  return (
    <Card title={`보유 종목 ${holdings.length}`}>
      <p className="mb-2 text-xs text-fg-subtle">
        종목 아래 회색 줄은 우리 시스템 기준선입니다 — 기간 범위 안 위치(마지막 일봉 종가 기준 · 0 = 바닥 · 100 = 꼭대기 · 종목 상세는 현재가로 다시 잽니다) · 종목 상세 "저점매수 사다리" 의 ★ 1단 · 모의 저점매수 계좌가 감시 중이면 그 매수 기준가와 손절선. 추천이 아닙니다.
      </p>
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th align="right">수량</Th>
            <Th align="right">평단가</Th>
            <Th align="right">현재가</Th>
            <Th align="right">평가금액</Th>
            <Th align="right">평가손익</Th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((holding) => (
            <tr key={holding.symbol}>
              {/*
                🔴 이름과 코드를 flex 로 묶는다 (2026-08-31)
                  ml-1 로 붙이면 좁은 화면에서 코드가 종목명을 밀어 이름이
                  두 줄로 접힌다. 감시 대상·섹터 표와 같은 규칙으로 맞췄다.
              */}
              <Td>
                <div className="flex items-baseline gap-1">
                  <StockLink
                    symbol={holding.symbol}
                    country={holding.marketCountry === 'US' ? 'US' : 'KR'}
                    className="font-medium text-fg"
                  >
                    {holding.name}
                  </StockLink>
                  <span className="tabular text-xs text-fg-subtle">{holding.symbol}</span>
                </div>
                {guides.get(holding.symbol) && (
                  <span className="mt-0.5 block text-xs text-fg-subtle">
                    {holdingGuideLine(guides.get(holding.symbol)!, (value) => money(value, holding.currency))}
                  </span>
                )}
              </Td>
              <Td align="right" numeric>
                {holding.quantity.toLocaleString('ko-KR')}
              </Td>
              <Td align="right" numeric>
                {money(holding.avgPurchasePrice, holding.currency)}
              </Td>
              <Td align="right" numeric>
                {money(holding.lastPrice, holding.currency)}
              </Td>
              <Td align="right" numeric>
                {money(holding.marketValue, holding.currency)}
                {/*
                  순평가액과의 차이를 작게 덧붙인다.
                  단타는 회전이 잦아 이 값이 누적되면 체감된다.
                */}
                <CostGap holding={holding} />
              </Td>
              <Td align="right" numeric className={changeColorClass(holding.profitLoss)}>
                {money(holding.profitLoss, holding.currency)}
                <span className="block text-xs">{formatPercent(holding.profitRate)}</span>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function CostGap({ holding }: { holding: Holding }) {
  const gap = costGap(holding.marketValue, holding.marketValueNet);

  if (gap === null || gap === 0) {
    return null;
  }

  return (
    <span className="block text-xs text-fg-subtle">
      비용 {money(gap, holding.currency)}
    </span>
  );
}

/**
 * 주문·체결 이력.
 *
 * 체결된 것만 보여주지 않는다. 취소한 주문도 그날의 기록이다.
 * "그날 뭘 하려다 말았는지" 가 남아야 복기가 된다.
 */
function OrderHistory() {
  const [page, setPage] = useState(0);
  const { data, isLoading, error, refetch } = useOrders(page, 20);

  return (
    <Card title="주문·체결 이력">
      {isLoading && <Spinner label="불러오는 중" />}
      {error && <ErrorState message={error.message} onRetry={() => void refetch()} />}

      {data && data.items.length === 0 && (
        <EmptyState
          title="주문 이력이 없습니다."
          description="체결 내역은 이 화면을 열어 둔 동안 1분마다 토스에서 받아 쌓습니다. 이 목록은 화면을 다시 열 때 · 페이지를 넘길 때 · [지금 동기화]를 누를 때 새로 읽습니다. 확정 스냅샷은 평일 16:00·20:10과 매일 06:10(미국장 뒤)입니다."
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>주문 시각</Th>
                <Th>종목</Th>
                <Th align="center">구분</Th>
                <Th align="right">수량</Th>
                <Th align="right">체결가</Th>
                <Th align="center">상태</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((order) => (
                <OrderRow key={order.orderId} order={order} />
              ))}
            </tbody>
          </Table>

          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}

function OrderRow({ order }: { order: TradeOrder }) {
  return (
    /*
      거부 레코드는 원주문과 중복이다. 토스가 취소·정정 거부 시
      별도 주문 레코드를 하나 더 만드는데 원주문도 목록에 그대로 있다.
      같은 무게로 그리면 한 거래가 두 번 보이므로 흐리게 둔다.
    */
    <tr className={cx(order.rejectionRecord && 'opacity-50')}>
      <Td numeric>
        <span className="text-xs">{formatDateTime(order.orderedAt)}</span>
      </Td>
      <Td>
        <StockLink symbol={order.symbol} className="tabular">
          {order.symbol}
        </StockLink>
      </Td>
      <Td align="center">
        <Badge tone={sideTone(order.side)}>{sideLabel(order.side)}</Badge>
      </Td>
      <Td align="right" numeric>
        {order.quantity.toLocaleString('ko-KR')}
        {/* 부분 체결이면 몇 주가 실제로 됐는지 보여준다 */}
        {order.filled && order.filledQuantity !== order.quantity && (
          <span className="block text-xs text-fg-subtle">
            체결 {order.filledQuantity.toLocaleString('ko-KR')}
          </span>
        )}
      </Td>
      <Td align="right" numeric>
        {order.filled
          ? money(order.avgFilledPrice, order.currency)
          : /* 미체결이면 주문가를 흐리게 보여준다. 시장가면 그것도 없다 */
            <span className="text-fg-subtle">
              {order.price === null ? '시장가' : money(order.price, order.currency)}
            </span>}
      </Td>
      <Td align="center">
        <Badge tone={statusTone(order)}>{statusLabel(order.status)}</Badge>
      </Td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  totalElements,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalElements: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return <p className="mt-3 text-xs text-fg-subtle">총 {totalElements}건</p>;
  }

  return (
    <div className="mt-3 flex items-center justify-between gap-2">
      <span className="text-xs text-fg-subtle">
        총 {totalElements}건 · {page + 1} / {totalPages}
      </span>
      <div className="flex gap-1">
        <Button variant="ghost" disabled={page === 0} onClick={() => onChange(page - 1)}>
          이전
        </Button>
        <Button
          variant="ghost"
          disabled={page >= totalPages - 1}
          onClick={() => onChange(page + 1)}
        >
          다음
        </Button>
      </div>
    </div>
  );
}

/**
 * 통화에 맞는 금액 포맷.
 *
 * 원화와 달러를 같은 함수로 찍으면 $ 금액에 원 이 붙는다.
 * 미장은 아직 미구현이지만 통화 필드는 이미 들어오고 있어 여기서 갈라둔다.
 */
function money(value: number | null, currency: string): string {
  if (currency === 'USD') {
    return formatUsd(value);
  }
  return formatKrw(value);
}
